/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageService, UtilsService } from '../../../../../core/services';
import { nameValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-adc-tags',
    templateUrl: './tags.component.html',
    styleUrls: ['./tags.component.scss'],
})

export class TagsComponent extends BaseComponent {
    /**
     * Max number of tags.
     */
    public maxTags = 32;

    /**
     * Store the incoming tags.
     */
    @Input() set tags(tags) {
        this._tags = tags && tags.length ? tags : [];
        if (this._tags.length >= this.maxTags) {
            this.formGroup.get('tagInput').disable();
        }
    }

    /**
     * Enable the form to be disabled externally.
     * @param state
     */
    @Input() set disableForm(state) {
        this._disableForm = state;
        const tagInput = this.formGroup.get('tagInput');
        if (state) {
            tagInput.disable({ emitEvent: false });
        } else {
            tagInput.enable({ emitEvent: false });
        }
    }

    /**
     * Set if the tags are editable or not.
     */
    @Input() public editMode: boolean = true;

    /**
     * Allow user to enter comma separated values.
     */
    @Input() public allowCSV: boolean = false;

    /**
     * Output list of tags.
     */
    @Output() public selectedTags: EventEmitter<any> = new EventEmitter();

    /**
     * Form group name.
     */
    public formGroup: FormGroup;

    /**
     * Check if tag input form is focused.
     * @private
     */
    private tagInputFocused: boolean = false;

    /**
     * Getter and Setter variable.
     * @private
     */
    private _tags: string[] = [];

    /**
     * Getter and Setter variable.
     * @private
     */
    private _disableForm: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForm();
        this.setUpFormSubscriber();
    }

    /**
     * Add a host listener for checking the enter key.
     * @param event
     */
    @HostListener('document:keydown.enter', ['$event'])
    public onKeydownHandler(event: KeyboardEvent): void {
        if (!this.tagInputFocused) {
            return;
        }
        if (event.key === 'Enter' && this.formGroup.valid && this.editMode) {
            const tagInput = this.formGroup.get('tagInput');
            if (this.isTagValid(tagInput) && !this.isEmptyString(tagInput)) {
                this.addTagToTagsArray(tagInput);
            }
        }
    }

    /**
     * Getter for tags.
     */
    get tags() {
        return this._tags;
    }

    /**
     * Getter for disableForm.
     */
    get disableForm() {
        return this._disableForm;
    }

    /**
     * Handle focus/blur of tag input field.
     * @param focused
     */
    public handleFocusBlur(focused: boolean): void {
        this.tagInputFocused = focused;
    }

    /**
     * Remove tag from tags array.
     */
    public removeTagFromTagsArray(tag: string): void {
        if (!this.disableForm) {
            const deleteIndex = this.tags.findIndex(item => item === tag);
            if (deleteIndex !== -1) {
                this.tags.splice(deleteIndex, 1);
                this.selectedTags.emit(this.tags);
                this.formGroup.get('tagInput').enable();
            }
        }
    }

    /**
     * Check if string contains only whitespace.
     * @param tagInput
     * @private
     */
    private isEmptyString(tagInput: any): boolean {
        return !tagInput.value.replace(/\s/g, '').length;
    }

    /**
     * Validate the tag
     * @param tagInput
     * @private
     */
    private isTagValid(tagInput: any): boolean {
        let message = '';
        if (this.tags.includes(tagInput.value.toLowerCase())) {
            message = 'tags_duplicate_message';
        } else if (this.tags.length >= this.maxTags) {
            message = 'tags_max_message';
        } else if (tagInput.value && tagInput.value.length === 0) {
            message = 'tags_min-length_message';
        } else if (tagInput.value && tagInput.value.length > 62) {
            message = 'tags_max-length_message';
        } else {
            return true;
        }

        tagInput.setErrors({ 'duplicateTags': this.languageService.translate(message) });
        tagInput.markAsTouched();
    }

    /**
     * Add tag(s) to tags array.
     * @param tagInput
     * @private
     */
    private addTagToTagsArray(tagInput: any): void {
        this.updateTagsArray(tagInput);
        this.updateTagFormInput(tagInput);
        this.selectedTags.emit(this.tags);
    }

    /**
     * Update the tags array.
     * @param tagInput
     * @private
     */
    private updateTagsArray(tagInput: any): void {
        const formValue = tagInput.value;
        if (this.allowCSV && formValue.includes(',')) {
            const values = formValue.split(',');
            for (const value of values) {
                this.tags.push(value);
            }
        } else {
            this.tags.push(formValue);
        }
    }

    /**
     * Update the tag form input field.
     * @param tagInput
     * @private
     */
    private updateTagFormInput(tagInput: any): void {
        tagInput.setValue('');
        tagInput.markAsUntouched();
        tagInput.markAsPristine();
        if (this.tags.length >= this.maxTags) {
            tagInput.disable();
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            tagInput: [[], [nameValidation, Validators.maxLength(63)]]
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setUpFormSubscriber(): void {
        const tagInput = this.formGroup.get('tagInput');
        tagInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('tagInput', () => {
                        tagInput.markAsTouched();
                        tagInput.updateValueAndValidity({ emitEvent: false });
                    }, 250);
                }
            });
    }
}

